import { Box, Button, InputAdornment, Menu, MenuItem, TextField } from "@mui/material";
import React, { useState } from "react";
import { ColorsConstants, inputColor } from "../cssUtils/constants";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import typography from "../cssUtils/typography";

/**
 * AppDropdown — outlined button with built-in dropdown menu.
 *
 * Props:
 *  - menuItems: Array<{ label: string, value: any }> — static list of options
 *  - selectedValue: any — currently selected value (shows checkmark)
 *  - onMenuSelect: (value: any, item: any) => void — called when a menu item is clicked
 *  - loading: boolean — shows "Loading..." state in the menu
 *  - emptyText: string — shown when menuItems is empty and not loading
 *  - paperProps: object — merged into Menu's PaperProps.style
 *  - renderItem: (item, isSelected, onSelect) => ReactNode — custom per-item renderer
 *  - menuContent: (closeMenu) => ReactNode — fully custom menu body (replaces menuItems entirely)
 *  - searchable: boolean — shows a search box at the top that filters menuItems by label
 *  - searchPlaceholder: string — placeholder for the search box
 *  - endIcon, startIcon, children, sx, variant — standard button props
 */
const AppDropdown = ({
  startIcon = null,
  endIcon = null,
  children,
  sx = {},
  variant = "outlined",
  menuItems = [],
  selectedValue = "",
  onMenuSelect,
  loading = false,
  emptyText = "No options available",
  paperProps = {},
  renderItem = null,
  menuContent = null,
  searchable = false,
  searchPlaceholder = "Search",
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");

  const handleButtonClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const term = search.trim().toLowerCase();
  const visibleItems = term
    ? menuItems.filter((item) => String(item.label).toLowerCase().includes(term))
    : menuItems;

  // onSelect(value, item) — item contains the full object incl. `raw` if set
  const handleMenuSelect = (value, item) => {
    onMenuSelect?.(value, item);
    handleMenuClose();
  };

  const renderMenuContent = () => {
    // Fully custom body — e.g. date picker panel
    if (menuContent) {
      return menuContent(handleMenuClose);
    }
    if (loading) {
      return <MenuItem disabled>Loading...</MenuItem>;
    }
    if (menuItems.length === 0) {
      return <MenuItem disabled>{emptyText}</MenuItem>;
    }
    if (visibleItems.length === 0) {
      return <MenuItem disabled>No results found</MenuItem>;
    }
    return visibleItems.map((item) => {
      const isSelected = selectedValue === item.value;
      if (renderItem) {
        return renderItem(item, isSelected, handleMenuSelect);
      }
      return (
        <MenuItem
          key={item.value}
          onClick={() => handleMenuSelect(item.value, item)}
          sx={{ ...typography.bodyLarge }}
        >
          {item.label}
          {isSelected && <CheckIcon sx={{ ml: 1 }} color="success" />}
        </MenuItem>
      );
    });
  };

  return (
    <>
      <Button
        variant={variant}
        size="medium"
        onClick={handleButtonClick}
        startIcon={startIcon}
        endIcon={endIcon}
        sx={{
          borderRadius: "8px",
          ...typography.bodyLarge,
          height: 40,
          textTransform: "none",
          border: `1px solid ${ColorsConstants.inputFieldGrey}`,
          color: inputColor,
          "&:hover": {
            border: `1px solid ${ColorsConstants.primaryGray50}`,
            backgroundColor: "transparent",
          },
          "&.Mui-focusVisible": {
            border: `1px solid ${ColorsConstants.primaryGray50}`,
          },
          ...sx,
        }}
        {...props}
        paperProps={paperProps}
      >
        {children}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        autoFocus={!searchable}
       PaperProps={{
        style: {
          minWidth: 'fit-content',
          width: anchorEl ? anchorEl.offsetWidth : undefined,
          ...paperProps,
        },
      }}
      >
        {searchable && !menuContent && (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={search}
              placeholder={searchPlaceholder}
              onChange={(e) => setSearch(e.target.value)}
              // Keep MenuList's type-ahead from stealing keystrokes
              onKeyDown={(e) => e.key !== "Escape" && e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        {renderMenuContent()}
      </Menu>
    </>
  );
};

export default AppDropdown;